import type { Connection, ConnectionState, TenantEvent } from '@convometry/contracts';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { routeUrl } from './api.js';
import { qk } from './query.js';

// Tenant event stream (SSE). The API authorizes the stream by cookie + membership and sends a `snapshot`
// first, then named events. QR payloads arrive ONLY here (manager+), are kept in memory, and are never
// persisted or logged by the client. If SSE keeps failing we fall back to polling.

export interface LiveConnection {
  state: ConnectionState;
  reason: string | null;
  historyProgress: number | null;
}

export interface LiveQr {
  qr: string;
  expiresAt: string;
}

export type LiveStatus = 'connecting' | 'open' | 'polling';

interface Snapshot {
  status: LiveStatus;
  connections: ReadonlyMap<string, LiveConnection>;
  qr: ReadonlyMap<string, LiveQr>;
}

export class LiveStore {
  private snap: Snapshot = { status: 'connecting', connections: new Map(), qr: new Map() };
  private listeners = new Set<() => void>();

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getSnapshot = () => this.snap;

  private emit(next: Partial<Snapshot>) {
    this.snap = { ...this.snap, ...next };
    this.listeners.forEach((fn) => fn());
  }
  setStatus(status: LiveStatus) {
    if (this.snap.status !== status) this.emit({ status });
  }
  setConnection(id: string, c: LiveConnection) {
    const connections = new Map(this.snap.connections);
    connections.set(id, c);
    // A QR is only meaningful while awaiting a scan.
    const qr = new Map(this.snap.qr);
    if (c.state !== 'awaiting_qr') qr.delete(id);
    this.emit({ connections, qr });
  }
  setQr(id: string, q: LiveQr) {
    const qr = new Map(this.snap.qr);
    qr.set(id, q);
    this.emit({ qr });
  }
  clearQr(id: string) {
    if (!this.snap.qr.has(id)) return;
    const qr = new Map(this.snap.qr);
    qr.delete(id);
    this.emit({ qr });
  }
}

const LiveContext = React.createContext<LiveStore | null>(null);
export const LiveProvider = LiveContext.Provider;

export function useLive(): Snapshot {
  const store = React.useContext(LiveContext);
  const fallback = React.useMemo(() => new LiveStore(), []);
  const s = store ?? fallback;
  return React.useSyncExternalStore(s.subscribe, s.getSnapshot, s.getSnapshot);
}

export function useLiveStore(): LiveStore | null {
  return React.useContext(LiveContext);
}

/** Merge live state into a connection row (live state wins while newer than the last fetch). */
export function withLiveState<T extends Pick<Connection, 'id' | 'state' | 'stateReason' | 'history'>>(row: T, live: ReadonlyMap<string, LiveConnection>): T {
  const l = live.get(row.id);
  if (!l) return row;
  return { ...row, state: l.state, stateReason: l.reason, history: { ...row.history, progress: l.historyProgress ?? row.history.progress } };
}

export function applyEvent(ev: TenantEvent, tenantId: string, store: LiveStore, qc: QueryClient, invalidate: (key: readonly unknown[]) => void) {
  switch (ev.type) {
    case 'snapshot':
      for (const c of ev.connections) {
        store.setConnection(c.connectionId, { state: c.state, reason: c.reason, historyProgress: c.historyProgress });
      }
      invalidate(qk.connections(tenantId));
      break;
    case 'connection.state': {
      const prev = store.getSnapshot().connections.get(ev.connectionId);
      store.setConnection(ev.connectionId, { state: ev.state, reason: ev.reason, historyProgress: ev.historyProgress });
      qc.setQueryData<{ items: Connection[] }>(qk.connections(tenantId), (old) =>
        old
          ? {
              items: old.items.map((c) =>
                c.id === ev.connectionId ? { ...c, state: ev.state, stateReason: ev.reason, history: { ...c.history, progress: ev.historyProgress ?? c.history.progress } } : c,
              ),
            }
          : old,
      );
      // State changes and history-import/backfill progress both change what the connections UI shows.
      if (!prev || prev.state !== ev.state || (ev.historyProgress !== null && prev.historyProgress !== ev.historyProgress)) {
        invalidate(qk.connections(tenantId));
        invalidate(qk.connection(tenantId, ev.connectionId));
      }
      break;
    }
    case 'connection.qr':
      store.setQr(ev.connectionId, { qr: ev.qr, expiresAt: ev.expiresAt });
      break;
    case 'conversation.updated':
      invalidate(qk.conversation(tenantId, ev.conversationId));
      invalidate(qk.conversations(tenantId));
      invalidate(qk.overview(tenantId));
      invalidate(qk.performance(tenantId));
      break;
    case 'analysis.updated':
      invalidate(qk.conversation(tenantId, ev.conversationId));
      invalidate(qk.conversations(tenantId));
      break;
    case 'alert.updated':
      invalidate(qk.alerts(tenantId));
      invalidate(qk.alertSummary(tenantId));
      for (const id of ev.alertIds) invalidate(qk.alert(tenantId, id));
      break;
  }
}

const EVENT_TYPES = ['snapshot', 'connection.state', 'connection.qr', 'conversation.updated', 'analysis.updated', 'alert.updated'] as const;

/** Opens the tenant event stream for the lifetime of the tenant layout. */
export function useTenantEventStream(tenantId: string, store: LiveStore) {
  const qc = useQueryClient();
  React.useEffect(() => {
    if (typeof EventSource === 'undefined') {
      store.setStatus('polling');
      return;
    }
    let es: EventSource | null = null;
    let failures = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let closed = false;

    // Coalesce invalidations: streams can emit bursts during history import.
    const pending = new Map<string, readonly unknown[]>();
    let flushTimer: ReturnType<typeof setTimeout> | undefined;
    const invalidate = (key: readonly unknown[]) => {
      pending.set(JSON.stringify(key), key);
      if (!flushTimer)
        flushTimer = setTimeout(() => {
          flushTimer = undefined;
          const keys = [...pending.values()];
          pending.clear();
          keys.forEach((k) => void qc.invalidateQueries({ queryKey: k }));
        }, 400);
    };

    const open = () => {
      if (closed) return;
      store.setStatus('connecting');
      es = new EventSource(routeUrl('events', { params: { tenantId } }), { withCredentials: true });
      es.onopen = () => {
        failures = 0;
        store.setStatus('open');
      };
      for (const type of EVENT_TYPES) {
        es.addEventListener(type, (msg) => {
          try {
            const data = JSON.parse((msg as MessageEvent<string>).data) as TenantEvent;
            applyEvent({ ...data, type } as TenantEvent, tenantId, store, qc, invalidate);
          } catch {
            // Ignore malformed events.
          }
        });
      }
      es.onerror = () => {
        failures++;
        if (failures >= 3) {
          // Give up on SSE for a while; pages poll instead (see usePollingInterval).
          es?.close();
          es = null;
          store.setStatus('polling');
          retryTimer = setTimeout(() => {
            failures = 0;
            open();
          }, 60_000);
        } else {
          store.setStatus('connecting');
        }
      };
    };
    open();
    return () => {
      closed = true;
      es?.close();
      if (retryTimer) clearTimeout(retryTimer);
      if (flushTimer) clearTimeout(flushTimer);
    };
  }, [tenantId, store, qc]);
}

/** Polling interval for live-ish data: only when the event stream is unavailable. */
export function usePollingInterval(ms: number): number | false {
  const { status } = useLive();
  return status === 'polling' ? ms : false;
}
